import { Injectable, Injector } from '@angular/core';
import { wrapWithLoader } from '@app/shared/common/decorators/wrap-loader.decorator';
import {
  ICreatedPdf,
  IPdfColumn,
  IPdfContent,
  IPdfDefinitions,
  IPdfGridData,
  IPdfGridOptions,
  IPdfGridValueParams,
  IPdfTools,
  PdfGenerateServiceV2,
  PdfSelectColumn,
  IPdfOptions,
  PdfData,
} from '@app/shared/common/make-pdf/v2';
import {
  getSelectedColumns,
  isEffectThisGrid,
} from '@app/shared/common/make-pdf/v2/utils/pdf-column-selector.utils';
import { getOrderedData } from '@app/shared/common/make-pdf/v2/utils/pdf-data-sort-filter.utils';
import { IInventoryItem } from '@app/shared/types';
import { ConvertCSVOptions, CsvSelectColumn } from './csv.type';
import {
  cloneDeep,
  every,
  fill,
  filter,
  forEach,
  get,
  head,
  includes,
  isEmpty,
  isFunction,
  keys,
  map,
  range,
  reduce,
  remove,
  set,
  tail,
} from 'lodash';
import { I18n } from '@app/i18n/strings-keys';

@Injectable()
export class CsvGenerateService extends PdfGenerateServiceV2 {
  public constructor(public injector: Injector) {
    super(injector);
  }

  public async downloadCsvAsync(definitions: IPdfDefinitions): Promise<void> {
    const csv = await this.createAsync(definitions);
    if (!csv) {
      return;
    }
    const csvContent = this.convertDataToCsv(csv, definitions);

    this.handleExportCsv(csv, csvContent);
  }

  public convertDataToCsv(
    csv: ICreatedPdf,
    definitions: IPdfDefinitions,
  ): string[][] {
    const definitionContent = get(definitions, 'content');
    const convertedContentData = reduce(
      keys(definitionContent),
      (mergedConvertedData, currentKey) => {
        const handleCsvDataMapping = {
          summary: (): string[][] =>
            this.convertSummaryToCsv(csv, definitions, currentKey),
          tips: (): string[][] =>
            this.convertTipsToCsv(csv, definitionContent, currentKey),
          grids: (): string[][] =>
            this.convertGridToCsv(csv, definitions, currentKey),
        };
        const contentKey = definitionContent[currentKey].type;
        if (isFunction(handleCsvDataMapping[contentKey])) {
          return [
            ...mergedConvertedData,
            ...handleCsvDataMapping[contentKey](),
          ];
        }

        return mergedConvertedData;
      },
      [],
    );

    return [...convertedContentData, [], ...this.generateFooter()];
  }

  public convertSummaryToCsv(
    csv: ICreatedPdf,
    definitions: IPdfDefinitions,
    currentKey: string,
  ): string[][] {
    const tools = this.pdfContentBuilder.getTools(definitions);
    const options = this.pdfContentBuilder.getOptionsWithDefault(definitions);
    const definitionContent = get(definitions, 'content');
    let currentColumnConfig;
    const settings = get(definitionContent, [currentKey, 'setting']);
    if (isFunction(settings)) {
      currentColumnConfig = get(settings(csv, options, tools), 'fields');
    } else {
      currentColumnConfig = get(definitionContent, [
        currentKey,
        'setting',
        'fields',
      ]);
    }

    return this.generateCSVSummaryWithSpace(
      csv,
      currentColumnConfig,
      definitions,
    );
  }

  public convertTipsToCsv(
    csv: ICreatedPdf,
    definitionContent: Record<string, IPdfContent>,
    currentKey: string,
  ): string[][] {
    const currentColumnConfig = get(definitionContent, [currentKey]);

    return isFunction(currentColumnConfig.setting)
      ? [this.translateMapperFnc(currentColumnConfig.setting(csv) as string[])]
      : [this.translateMapperFnc(currentColumnConfig.setting as string[])];
  }

  public convertGridToCsv(
    csv: ICreatedPdf,
    definitions: IPdfDefinitions,
    currentKey: string,
  ): string[][] {
    const definitionContent = get(definitions, 'content');
    // TODO: capability to setting not a function
    const gridsSetting = (definitionContent[currentKey].setting as Function)(
      csv.pdfData,
    );

    const options = this.pdfContentBuilder.getOptionsWithDefault(definitions);
    const tools = this.pdfContentBuilder.getTools(definitions);

    return reduce(
      keys(gridsSetting),
      (mergedConvertedData, key) => {
        const gridData = get(csv, `pdfData.${key}`);
        const currentColumnConfig = get(gridsSetting, [key, 'columns']);
        const gridTitle = get(gridsSetting, [key, 'title']);

        // Check if this grid should be hidden
        const hiddenPredicate = get(gridsSetting, [key, 'hidden']);
        if (isFunction(hiddenPredicate)) {
          const isHidden = hiddenPredicate({
            data: csv.pdfData,
            options,
            tools,
          });
          if (isHidden) {
            // Skip this grid if it's hidden
            return mergedConvertedData;
          }
        } else if (hiddenPredicate === true) {
          // Skip if hidden is explicitly true
          return mergedConvertedData;
        }

        const gridOptions: IPdfGridOptions = {
          gridData,
          grid: get(gridsSetting, key),
          fieldPath: key,
          contentKey: 'mainGrids',
        };
        const isEffectForThisGrid = isEffectThisGrid(gridOptions, options);
        const removedHiddenColumns = this.removeHiddenColumns(
          { columnConfig: currentColumnConfig, data: csv.pdfData },
          options,
          isEffectForThisGrid,
        );

        const convertedOptions = {
          gridTitle,
          tools,
          fieldPath: key,
          isTreeView: csv.pdfData.isTreeView,
          selectedColumns: removedHiddenColumns,
          columnConfig: currentColumnConfig,
        };
        const orderBy = get(gridsSetting, [key, 'orderBy']);
        if (isEffectForThisGrid) {
          // notice: convertEffectGridToCsv don`t use ordered grid data.
          return [
            ...mergedConvertedData,
            [],
            ...this.convertEffectGridToCsv(gridData, convertedOptions),
          ];
        }

        return [
          ...mergedConvertedData,
          [],
          ...this.convertUnaffectedGridToCsv(
            getOrderedData(gridData, orderBy),
            convertedOptions,
          ),
        ];
      },
      [],
    );
  }

  public generateCSVSummaryWithSpace(
    csv: ICreatedPdf,
    columnConfig: IPdfColumn[],
    definitions: IPdfDefinitions,
  ): string[][] {
    const csvSummaryWithoutSpace = this.generateCSVSummaryWithoutSpace(
      csv,
      columnConfig,
      definitions,
    );
    const spacedTitle = this.handleTitleSpace(csvSummaryWithoutSpace, 3);
    const header = this.i18nService.instant(
      get(head(get(csv, 'pdfMakeDefinition.header')), 'text'),
    );

    return [[header], ...spacedTitle, []];
  }

  public generateCSVSummaryWithoutSpace(
    csv: ICreatedPdf,
    columnConfig: IPdfColumn[],
    definitions: IPdfDefinitions,
  ): string[][] {
    const pdfDefinition = this.getDefinition(definitions, null);
    const tools = this.pdfContentBuilder.getTools(pdfDefinition);

    return map(this.removeHiddenColumnConfig(csv, columnConfig), (column) => {
      const summaryKeys = keys(column);

      return map(summaryKeys, (currentKey) => {
        const currentValue = get(
          csv.pdfData,
          get(column, [currentKey, 'path']),
        );

        if (isFunction(column[currentKey]?.hidden)) {
          const hidden = column[currentKey].hidden({
            data: csv.pdfData,
            value: currentValue,
          });
          if (hidden) return '';
        }

        if (isFunction(column[currentKey]?.valueGetter)) {
          const getterValue = column[currentKey].valueGetter({
            tools,
            data: csv.pdfData,
            value: currentValue,
          });

          return this.generateSummaryKeyAndValue(currentKey, getterValue);
        }

        if (isFunction(column[currentKey]?.valueFormatter)) {
          const formattedValue = column[currentKey].valueFormatter({
            tools,
            data: csv.pdfData,
            value: currentValue,
          });

          return this.generateSummaryKeyAndValue(currentKey, formattedValue);
        }

        return this.generateSummaryKeyAndValue(currentKey, currentValue);
      });
    });
  }

  public removeHiddenColumnConfig(
    csv: ICreatedPdf,
    columnConfig: IPdfColumn[],
  ): IPdfColumn[] {
    const getHiddenConfigs = map(columnConfig, (column) => {
      const summaryKeys = keys(column);

      return map(summaryKeys, (currentKey) => {
        const currentValue = get(
          csv.pdfData,
          get(column, [currentKey, 'path']),
        );
        let hidden = false;
        if (isFunction(column[currentKey]?.hidden)) {
          hidden = column[currentKey].hidden({
            data: csv.pdfData,
            value: currentValue,
          });
        }

        return hidden;
      });
    });
    const hiddenIndexList: number[] = [];

    forEach(getHiddenConfigs, (getHiddenConfig, index) => {
      if (every(getHiddenConfig, (hidden) => hidden)) {
        hiddenIndexList.push(index);
      }
    });

    return filter(
      columnConfig,
      (column, index) => !includes(hiddenIndexList, index),
    );
  }

  public generateSummaryKeyAndValue(
    summaryKey: string,
    summaryValue: string,
  ): string {
    return `${this.i18nService.instant(summaryKey)}: ${summaryValue}`;
  }

  public handleTitleSpace(
    cscTitle: string[][],
    columnSpace: number,
  ): string[][] {
    return map(cscTitle, (item) => {
      const titleContainer = fill(range(7), '');
      forEach(item, (ele, idx) => {
        set(titleContainer, idx * columnSpace, ele);
      });

      return titleContainer;
    });
  }

  public translateMapperFnc(columnList: string[]): string[] {
    return map(columnList, (column) => this.i18nService.instant(column));
  }

  public convertEffectGridToCsv(
    inventoryItems: IInventoryItem,
    options: ConvertCSVOptions,
  ): string[][] {
    const selectedColumns = options.selectedColumns;
    const convertedColumnResult = this.convertColumnConfig(
      selectedColumns,
      options,
    );
    const columnTitle = this.translateMapperFnc(head(convertedColumnResult));

    const selectedColumnPath = head(tail(convertedColumnResult));

    const tableContent = map(
      this.csvValueGetter(inventoryItems, selectedColumns, options.tools),
      (rowData) => map(selectedColumnPath, (path) => get(rowData, path, '')),
    );

    return [columnTitle, ...tableContent];
  }

  public convertUnaffectedGridToCsv(
    totals: IPdfGridData[],
    options: ConvertCSVOptions,
  ): string[][] {
    const items = cloneDeep(totals);
    const selectedColumns = options.selectedColumns;
    const columnTitle = this.translateMapperFnc(
      map(selectedColumns, (item) => item.title),
    );
    const selectedColumnPath = map(selectedColumns, (column) => column.path);
    const configPath = map(options.columnConfig, (column) => column.path);
    const configTitle = this.translateMapperFnc(
      map(options.columnConfig, (column) => column.title),
    );

    const filteredTitle = filter(columnTitle, (title) =>
      includes(configTitle, title),
    );
    const filteredPath = filter(selectedColumnPath, (path) =>
      includes(configPath, path),
    );

    const tableContent = map(
      this.csvValueGetter(items, selectedColumns, options.tools),
      (rowData) => map(filteredPath, (path) => get(rowData, path, '')),
    );

    return options.gridTitle && !isFunction(options.gridTitle)
      ? [
          [this.i18nService.instant(get(options.gridTitle, 'text'))],
          filteredTitle,
          ...tableContent,
        ]
      : [filteredTitle, ...tableContent];
  }

  public generateFooter(): string[][] {
    const year = new Date().getFullYear();

    return [
      [
        `© ${year} ${this.i18nService.instant(I18n.InfoMessages.AllRightsClaim)}`,
      ],
      [this.i18nService.instant(I18n.InfoMessages.ExportFooterText)],
    ];
  }

  public convertColumnConfig(
    selectedColumns,
    options: ConvertCSVOptions,
  ): string[][] {
    let columnTitle = map(selectedColumns, (item) => item.title);
    let selectedColumnPath = remove(
      map(selectedColumns, (column) => column.path),
      (item) => !isEmpty(item),
    );
    if (options.isTreeView) {
      columnTitle = [
        head(columnTitle),
        this.i18nService.instant(I18n.Forms.GridForm.MajorCategory),
        this.i18nService.instant(I18n.Forms.GridForm.MinorCategory),
        ...tail(columnTitle),
      ];
      selectedColumnPath = [
        head(selectedColumnPath),
        'major_category_name',
        'minor_category_name',
        ...tail(selectedColumnPath),
      ];
    }

    return [columnTitle, selectedColumnPath];
  }

  public csvValueGetter(
    inventoryItems: IInventoryItem,
    columnConfig: CsvSelectColumn[],
    tools: IPdfTools,
  ): IInventoryItem {
    const csvGetteredValue: IInventoryItem = map(inventoryItems, (item) => {
      return reduce(
        columnConfig,
        (formattedItem, column) => {
          let columnValue = column.valueGetter
            ? column.valueGetter({
                tools,
                rowData: item,
                value: item[column.path],
              } as IPdfGridValueParams)
            : item[column.path];

          columnValue = column.valueFormatter
            ? column.valueFormatter({
                tools,
                rowData: { ...item, [column.path]: columnValue },
                value: columnValue,
              } as IPdfGridValueParams)
            : columnValue;

          return { ...formattedItem, [column.path]: columnValue };
        },
        { ...item },
      );
    });

    return csvGetteredValue;
  }

  public removeHiddenColumns(
    { columnConfig, data }: { columnConfig: IPdfColumn[]; data: PdfData },
    options: IPdfOptions,
    isEffectForThisGrid: boolean,
  ): PdfSelectColumn[] {
    return filter(
      isEffectForThisGrid
        ? getSelectedColumns(columnConfig, options)
        : columnConfig,
      (column) => {
        if (isFunction(column.hiddenColumn)) {
          return !column?.hiddenColumn({
            data,
            options: {
              selectedColumns: options.selectedColumns,
            },
          } as IPdfGridValueParams);
        }

        return !column.hiddenColumn;
      },
    );
  }

  @wrapWithLoader('options.isShownLoadingWhenGenerate', 0, true)
  public handleExportCsv(csv: Partial<ICreatedPdf>, csvData: string[][]): void {
    let csvString = csvData.map((data) => data.join('\t')).join('\r\n');
    // add CSV file flag
    csvString = `data:text/csv;charset=utf-8,\uFEFF${encodeURIComponent(csvString)}`;
    const link = document.createElement('a');
    const filename = csv.filename;
    link.href = csvString;
    link.download = `${filename}.csv`;
    link.click();
    link.remove();
  }
}
